import { filter, find, get } from 'lodash';
import { IMergedStudent } from 'src/ng2/shared/services/mad-lib-services/grouping-functions/postsec-groupings/college-plan-grouping';
import { IStudentSupport } from 'Src/ng2/shared/typings/interfaces/student-support.interface';
import { IPointPerson, IStudent } from 'Src/ng2/shared/typings/interfaces/student.interface';
import { IStudentPath, IStudentWithPaths } from 'src/ng2/shared/typings/interfaces/studentPaths.interface';
import { IFlag, TValidFlagTypes } from '../../shared/typings/interfaces/flags.interface';
import { TValidFlagCategories } from '../../shared/typings/types/flag.types';
import { COLUMN_CALCULATION_TYPE, COLUMN_DATA_TYPE } from './../../shared/constants/list-view/cell-type.constant';
import { SubjectAreas } from './../../shared/constants/subject-areas.constant';
import {
  POSTSEC_COLUMNS,
  POSTSEC_GRAPH_COLUMNS,
} from './../lists/postsecondary-list/postsecondary-list-mad-lib/postsecondary-mad-lib.constant';

export const students = {
  human: 'Students',
  key: 'studentName',
  cellType: COLUMN_DATA_TYPE.SECTION_HEADER,
  cellConfig: {},
  dataType: 'String',
  orderBy: 'asc',
  getValueAtPath: (student: { studentId; studentDetails: { name: { lastFirst: string } } }) => {
    return {
      data: student.studentDetails.name.lastFirst,
      dependencies: { studentId: student.studentId },
    };
  },
};

export const pointPeople = {
  human: 'PP',
  key: 'pointPerson',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  dataType: 'String',
  orderBy: 'asc',
  getValueAtPath: (
    student: { pointPeople: IPointPerson[] },
    flagCategory: TValidFlagCategories,
    flagType: TValidFlagTypes,
  ) => {
    let person = find(student.pointPeople, (pointPerson: IPointPerson) => pointPerson.type === flagCategory);
    const fullName = person ? `${person.user.firstName[0]}${person.user.lastName[0]}` : null;
    const data = fullName;
    const dependencies = {};
    return { data, dependencies };
  },
};

/* istanbul ignore next */
export const when = {
  human: 'When',
  key: 'when',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  dataType: 'Number',
  orderBy: 'asc',
  getValueAtPath: (student: { flags: IFlag[] }, flagCategory: TValidFlagCategories, flagType: TValidFlagTypes) => {
    let data;
    const { flags } = student;

    const flag: any = find(flags, { type: flagType });
    const dependencies = { filterValue: null };
    if (flag) {
      const { status, ignoredAt, createdAt, isIgnored, supportAddedAt, otherActionAddedAt } = flag;

      if (status === 'ACTIVE') {
        if (isIgnored) data = ignoredAt;
        else data = createdAt;
      } else {
        if (supportAddedAt && !otherActionAddedAt) data = supportAddedAt;
        if (!supportAddedAt && otherActionAddedAt) data = otherActionAddedAt;
        if (supportAddedAt && otherActionAddedAt) {
          data = moment(flag.supportAddedAt).isBefore(flag.otherActionAddedAt) ? supportAddedAt : otherActionAddedAt;
        }
      }
      const today = moment(new Date());
      const flagDate = moment(data);
      const daysDiff = today.diff(flagDate, 'days');

      data = daysDiff > 0 ? `${daysDiff}d` : `Today`;
      dependencies.filterValue = daysDiff;
    }
    data = data ? data : null;
    return { data, dependencies };
  },
};

export const notes = {
  human: 'Notes',
  key: 'notes',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  dataType: 'String',
  orderBy: 'asc',
  getValueAtPath: (student: { flags: IFlag[] }, flagCategory: TValidFlagCategories, flagType: TValidFlagTypes) => {
    const { flags } = student;
    const flag: IFlag = find(flags, { type: flagType });
    // flag.note is an optional field an will likely be undefined, if so set to null for list-formatting
    const flagNote = flag ? flag.note : null;
    const data = flagNote || null;
    const dependencies = { filterValue: flagNote ? flagNote.toLowerCase() : null };
    return { data, dependencies };
  },
};

export const ytd = {
  human: 'Ytd',
  key: 'ytd',
  cellType: COLUMN_DATA_TYPE.PERCENTAGE,
  cellConfig: {},
  dataType: 'Number',
  orderBy: 'asc',
  getValueAtPath: (student: { att: { ytd: number } }) => {
    let data = get(student, 'att.ytd') || 0;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const today = {
  human: 'Today',
  key: 'today',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  dataType: 'String',
  orderBy: 'asc',
  getValueAtPath: (student: { att: { today: string } }) => {
    const data = get(student, 'att.today') || null;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const supports = {
  human: 'Supports',
  key: 'supports',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  dataType: 'Number',
  orderBy: 'asc',
  getValueAtPath: (
    student: { activeStudentSupports: IStudentSupport[] },
    flagCategory: TValidFlagCategories,
    flagType: TValidFlagTypes,
  ) => {
    const studentSupports: IStudentSupport[] = get(student, 'activeStudentSupports');
    const studentSupportsForCat = studentSupports.filter(
      studentSupport => studentSupport.support.category === flagCategory,
    );
    const data = studentSupportsForCat.length;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const flags = {
  human: 'Flags',
  key: 'flags',
  cellType: COLUMN_DATA_TYPE.FLAG,
  cellConfig: {},
  dataType: 'Number',
  orderBy: 'asc',
  getValueAtPath: (student: { flags: IFlag[] }, flagCategory: TValidFlagCategories, flagType: TValidFlagTypes) => {
    const flags: IFlag[] = get(student, 'flags');
    const flagForCat = flags.filter(flag => flag.category === flagCategory);
    const data = flagForCat;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const failing = {
  human: 'Total Failing',
  path: 'currProgram.countFailing',
  key: 'failing',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GRADIENT_CUSTOM,
  cellConfig: {},
  getValueAtPath: student => {
    let data = get(student, 'currProgram.countFailing');
    const dependencies = {};
    return { data, dependencies };
  },
};

export const course = {
  human: 'Course',
  path: null,
  key: 'course',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  getValueAtPath: (
    student: { currProgram: { grades: Array<{ code: string; section: 'string'; mostRecent: boolean; pf: string }> } },
    courseCode: string,
  ) => {
    const {
      currProgram: { grades },
    } = student;
    const { section, code } = find(
      grades,
      ({ code, mostRecent, pf }) => pf === 'F' && code === courseCode && mostRecent,
    );
    const data = `${code}-${section}`;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const latestGrade = {
  human: 'Latest grade',
  path: null,
  key: 'latestGrade',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  getValueAtPath: (
    student: { currProgram: { grades: Array<{ code: string; mostRecent: boolean; str: string; pf: string }> } },
    config,
  ) => {
    const { courseCode } = config;
    const {
      currProgram: { grades },
    } = student;
    const { str } = find(grades, ({ code, mostRecent, pf }) => pf === 'F' && code === courseCode && mostRecent);
    const data = str;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const priority = {
  human: 'Priority',
  path: 'tbd',
  key: 'priority',
  dataType: 'String',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  getValueAtPath: (
    student: { currProgram: { grades: Array<{ code: string; mostRecent: boolean; priority: string; pf: string }> } },
    config,
  ) => {
    const { courseCode } = config;
    const {
      currProgram: { grades },
    } = student;
    const { priority } = find(grades, ({ code, mostRecent, pf }) => pf === 'F' && code === courseCode && mostRecent);
    const data = priority;
    let filterValue;
    if (priority === 'high') filterValue = 3;
    else if (priority === 'medium') filterValue = 2;
    else filterValue = 1;
    const dependencies = { filterValue };
    return { data, dependencies };
  },
};

export const needsSubjectSupport = {
  human: 'Needs Support',
  path: 'activeStudentSupports',
  key: 'supports',
  dataType: 'String',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  getValueAtPath: (student, config) => {
    const { courseCode, subject } = config;
    const selectedSubjectHumanShort = SubjectAreas[subject].humanShort;
    const {
      currProgram: { grades },
      activeStudentSupports,
    } = student;
    const courseData: any = find(grades, { code: courseCode });
    const { priority } = courseData;

    const filteredStudentSupports = activeStudentSupports.filter(
      studentSupport =>
        studentSupport.support.category === 'ACADEMIC' &&
        studentSupport.support.metaData &&
        studentSupport.support.metaData.subject === selectedSubjectHumanShort,
    );
    const needsSupport = !filteredStudentSupports.length && priority === 'high';
    const data = needsSupport ? 'Yes' : '-';
    const dependencies = {};
    return { data, dependencies };
  },
};

export const gpa = {
  human: 'GPA',
  key: 'GPA',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  getValueAtPath: (student: IStudent) => {
    const data = student.creditDetails.GPA;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const cunyCollegeReadiness = {
  human: 'CUNY COLLEGE READINESS',
  key: 'CUNY_COLLEGE_READINESS',
  dataType: 'String',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.CUNY_COLLEGE_READINESS,
  cellConfig: {},
  getValueAtPath: (student: IStudent) => {
    const { isELACR, isMathCR } = student.postsecondary;
    let cunyVal;
    if (isELACR && isMathCR) cunyVal = '2/2';
    else if ((isELACR && !isMathCR) || (!isELACR && isMathCR)) cunyVal = '1/2';
    else cunyVal = '0/2';

    const data = {
      student,
      calculatedStatus: cunyVal,
    };
    const dependencies = { filterValue: cunyVal };
    return { data, dependencies };
  },
};

export const maxPSAT = {
  human: 'MAX PSAT',
  key: 'MAX_PSAT',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  getValueAtPath: (student: IStudent) => {
    const data = student.postsecondary.PSAT.totalMax;
    // TODO: Tooltip needed like in postsec panel? (JE)
    const dependencies = {};
    return { data, dependencies };
  },
};

export const maxSAT = {
  human: 'MAX SAT',
  key: 'MAX_SAT',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  getValueAtPath: (student: IStudent) => {
    const data = student.postsecondary.SAT.total1600Max;
    // TODO: Tooltip needed like in postsec panel? (JE)
    const dependencies = {};
    return { data, dependencies };
  },
};

export const two_year = {
  human: '2 year',
  key: 'two_year',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  getValueAtPath: (student: IStudentWithPaths) => {
    const { studentPaths } = student;
    const twoYearColleges = filter(studentPaths, (studentPath: IStudentPath) => {
      const {
        path: { years, type },
      } = studentPath;
      return type === 'COLLEGE' && years === 2;
    });
    const data = twoYearColleges.length;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const four_year = {
  human: '4 year',
  key: 'four_year',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  getValueAtPath: (student: IStudentWithPaths) => {
    const { studentPaths } = student;
    const fourYearColleges = filter(studentPaths, (studentPath: IStudentPath) => {
      const {
        path: { years, type },
      } = studentPath;
      return type === 'COLLEGE' && years === 4;
    });
    const data = fourYearColleges.length;
    const dependencies = {};
    return { data, dependencies };
  },
};

export const maxACT = {
  human: 'MAX ACT',
  key: 'MAX_ACT',
  dataType: 'Number',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.GENERIC,
  cellConfig: {},
  getValueAtPath: (student: IStudent) => {
    const data = student.postsecondary.ACT.compositeMax;
    // TODO: Tooltip needed like in postsec panel? (JE)
    const dependencies = {};
    return { data, dependencies };
  },
};

export const postsecPointPerson = {
  human: 'Point Person',
  key: 'PP',
  dataType: 'String',
  orderBy: 'desc',
  cellType: COLUMN_DATA_TYPE.POINT_PERSON,
  cellConfig: {},
  getValueAtPath: (student: IStudent) => {
    const pp = find(student.pointPeople, (pointPerson: IPointPerson) => {
      return pointPerson.type === 'POSTSECONDARY';
    });
    const data = pp ? `${pp.user.firstName} ${pp.user.lastName}` : null;
    const dependencies = {};
    return { data, dependencies };
  },
  tooltip: 'Postsecondary Point Person.',
};

const FLAG_BASED_NOT_PP = [students, pointPeople, when, notes];

const FLAG_BASED_PP = [students, when, notes];

export const INFORMATIONAL_NOT_PP = [students, pointPeople, ytd, today, supports, flags];

export const INFORMATIONAL_PP = [students, ytd, today, supports, flags];

const failureWithNoSupports = [students, latestGrade, priority, failing, needsSubjectSupport];

export const STUDENT_LEVEL_LIST_COLUMNS = {
  POST_SECONDARY_FLAGS: {
    GRADE: FLAG_BASED_NOT_PP,
    CLASS_OF: FLAG_BASED_NOT_PP,
    POINT_PERSON: FLAG_BASED_PP,
    GRAD_PLAN: FLAG_BASED_NOT_PP,
  },
  POST_SECONDARY_PLANS: {
    GRADE: FLAG_BASED_NOT_PP,
    CLASS_OF: FLAG_BASED_NOT_PP,
    POINT_PERSON: FLAG_BASED_PP,
    GRAD_PLAN: FLAG_BASED_NOT_PP,
  },
  FAILURES_WITH_NO_SUPPORT: {
    SUBJECT: failureWithNoSupports,
    COURSE: failureWithNoSupports,
    TEACHER: failureWithNoSupports,
  },
};
